import { useTranslation } from 'react-i18next';
import { umbralAlcanzado } from './racha.ts';

interface Props {
  racha: number;
  umbrales: readonly number[];
  /** `completo` añade el número de racha; `compacto` es solo el distintivo. */
  variante?: 'compacto' | 'completo';
}

/**
 * Distintivo de racha viva, calculado al vuelo desde la racha actual.
 *
 * No es una medalla: no hay tabla de logros ni "conseguido el día tal". Si la racha se
 * rompe, este componente deja de pintar nada. Es el estado de ahora.
 *
 * El `title` dice el multiplicador de forma indirecta ("tus pronósticos valen más"): el
 * valor exacto sale de `points_config` y no está aquí, así que anunciarlo sería escribirlo
 * en un segundo sitio.
 */
export function RachaBadge({ racha, umbrales, variante = 'compacto' }: Props) {
  const { t } = useTranslation();
  const umbral = umbralAlcanzado(racha, umbrales);
  if (umbral === null) return null;

  return (
    <span
      className="inline-flex items-center gap-1 rounded-full border border-acento px-2 py-0.5 text-xs font-bold text-acento"
      title={t('streak.badgeTitle', { count: racha })}
      data-test="racha-badge"
      data-umbral={umbral}
    >
      <span aria-hidden="true">🔥</span>
      {variante === 'completo' ? t('streak.badge', { count: racha }) : racha}
    </span>
  );
}

/**
 * La racha en texto, se tenga distintivo o no. En la clasificación acompaña al resto de
 * componentes del desempate; en pronósticos es la cabecera de la pantalla.
 */
export function RachaTexto({ racha }: { racha: number }) {
  const { t } = useTranslation();
  return (
    <span data-test="racha-valor" data-racha={racha}>
      {racha === 0 ? t('streak.none') : t('streak.count', { count: racha })}
    </span>
  );
}
